import { graphql } from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";
import styled from "styled-components";
import { MultipleChoiceElementDisplay_MultipleChoiceElementV2Fragment$key } from "@generated/MultipleChoiceElementDisplay_MultipleChoiceElementV2Fragment.graphql";
import { isWissenwirktApp } from "../../../utils/isWissenwirktApp";
const MULTIPLE_CHOICE_ELEMENT_FRAGMENT = graphql`
	fragment MultipleChoiceElementDisplay_MultipleChoiceElementV2Fragment on MultipleChoiceLearnElement {
		question
		image {
			url
		}
		answeringType
		answerOptions {
			answer
			image {
				url
			}
		}
	}
`;

export interface MultipleChoiceState {
	checkedAnswerIndices: number[];
}

const Image = styled.img`
	max-width: 50%;
`;

const AnswerContainer = styled.div`
	max-width: 1024px;
`;

const ASCII = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

interface OwnProps {
	checkedAnswerIndices: number[];
	setCheckedAnswerIndices: (indices: number[]) => void;
	multipleChoiceElementFragmentRef: MultipleChoiceElementDisplay_MultipleChoiceElementV2Fragment$key;
	currentElementIndex: number;
	className?: any;
}

export function MultipleChoiceElementDisplay({
	checkedAnswerIndices,
	setCheckedAnswerIndices,
	multipleChoiceElementFragmentRef,
	currentElementIndex,
	className,
}: OwnProps) {
	const multipleChoiceElement =
		useFragment<MultipleChoiceElementDisplay_MultipleChoiceElementV2Fragment$key>(
			MULTIPLE_CHOICE_ELEMENT_FRAGMENT,
			multipleChoiceElementFragmentRef,
		);

	if (!multipleChoiceElement) return null;

	const toggleAnswer = (index: number) => {
		const newIndices =
			multipleChoiceElement.answeringType === "allowOnlyOneAnswer"
				? []
				: checkedAnswerIndices.filter((x) => x !== index);
		setCheckedAnswerIndices([
			...newIndices,
			...(checkedAnswerIndices.includes(index) ? [] : [index]),
		]);
	};
	const selectedAnswerBackground = () => {
		if (!isWissenwirktApp) {
			return "bg-bluegray-200";
		} else {
			return "bg-primary";
		}
	};
	return (
		<div className="p-5">
			{isWissenwirktApp && <h3 className="text-center"> Lernerfolgskontrolle</h3>}
			<TextQuestion
				dangerouslySetInnerHTML={{
					__html: `${currentElementIndex + 1}. Frage: ${multipleChoiceElement.question}`,
				}}
				className={`font-bold text-base lg:text-xl ${className}`}
			/>
			{multipleChoiceElement.image?.url && (
				<Image className="mb-5" src={multipleChoiceElement.image.url} alt="" />
			)}
			<div className={`mb-3 mt-2 ${className}`}>
				{multipleChoiceElement.answeringType === "allowOnlyOneAnswer" && (
					<strong className=" text-base lg:text-lg">Bitte wählen Sie EINE Antwort aus.</strong>
				)}
				{multipleChoiceElement.answeringType === "allowMultipleAnswersAllCorrectMustBeGiven" && (
					<strong className="text-base lg:text-lg">
						Bitte wählen Sie ALLE zutreffenden Antworten aus.
					</strong>
				)}
				{multipleChoiceElement.answeringType ===
					"allowMultipleAnswersOnlySomeCorrectMustBeGiven" && (
					<strong className="text-base lg:text-lg">
						Bitte wählen Sie die richtigen Antworten aus.
					</strong>
				)}
			</div>
			<AnswerContainer>
				{multipleChoiceElement.answerOptions?.map((ao, index) => (
					<div
						key={`answerOption-${index}`}
						className="cursor-pointer"
						onClick={() => {
							toggleAnswer(index);
						}}
					>
						<div
							key={ao.answer + index}
							className={`${
								checkedAnswerIndices.includes(index) ? selectedAnswerBackground() : ""
							} border-round surface-200 border-400 border-1 mb-3 p-2 lg:p-4`}
						>
							<div className="flex align-items-center">
								<div className="pl-3 pr-5 font-bold flex align-items-center text-lg ">
									{ASCII[index]}
								</div>
								<div
									className="flex-grow-1 flex flex-column align-items-start"
									dangerouslySetInnerHTML={{ __html: ao.answer }}
								/>
							</div>
							{ao.image?.url && <Image className="ml-3 mt-3" src={ao.image.url} alt="" />}
						</div>
					</div>
				))}
			</AnswerContainer>
		</div>
	);
}
const TextQuestion = styled.h1`
	div {
		display: inline !important;
	}
`;
